import * as React from 'react'
import CloseGray from '../../../assets/icons_new/CloseGray.svg'
import { faCircleDashedRegular, faCircleXRegular, FaIcon, faTrash } from '@/lib/fa'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toasts/api'
import useCompanyService from '@/modules/drivers/services/CompanyService'
import { CompanyType } from '@/app/constants/companyType'
import { useHasAccess } from '@/modules/shared/permissions'

interface CompanySelectedActionBarProps {
  selectedGroup: SelectionGroup
  resetAll: () => void
  companyId: string
  onEdit?: () => void
  onDuplicate?: () => void
}

const CompanySelectedActionBar: React.FC<CompanySelectedActionBarProps> = ({
  selectedGroup,
  resetAll,
  companyId,
  onEdit,
  onDuplicate,
}) => {
  const { activateCompany, deleteCompany } = useCompanyService()
  const { allowed: canRemoveCompany } = useHasAccess({ permissions: 'canRemoveCompany' }, companyId)
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const { data: companies = [] } = useQueryCompanies({
    includeDeleted: false,
  })
  // Mirrors CompaniesTable — service companies are hidden, so they must not
  // inflate the "Ilość firm" counter either.
  const totalCount = companies.filter((c) => c.type !== CompanyType.SERVICE).length

  // Get selected companies to check their active status
  const selectedCompanies = companies.filter((c) =>
    selectedGroup?.ids.includes(c.id),
  )
  const isSelectedActive = selectedCompanies.every(
    (c) => c.isActive !== false && c.deletedDate == null,
  )

  const handleActivate = async (isActive: boolean) => {
    const companyIds = (selectedGroup?.ids || []).map((id) => String(id))
    if (companyIds.length === 0) return
    try {
      const result = await activateCompany(companyIds, isActive)
      if (result && typeof result === 'object' && 'code' in result) {
        toast.error({ text: t('addCompany.groupMembersUpdateError') })
        return
      }
      await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
      toast.success({ text: t('addCompany.groupMembersUpdated') })
      resetAll()
    } catch {
      toast.error({ text: t('addCompany.groupMembersUpdateError') })
    }
  }

  const handleDelete = async () => {
    const companyIds = selectedGroup?.ids.map((id) => String(id)) || []
    if (companyIds.length === 0) return
    try {
      await deleteCompany(companyIds)
      await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
      toast.success({ text: t('addCompany.companiesDeleted') })
      resetAll()
    } catch {
      toast.error({ text: t('addCompany.companiesDeleteError') })
    }
  }

  return (
    <div className="tw-relative tw-flex tw-w-full tw-justify-between tw-border-b-2 tw-border-primary tw-bg-[#F1F4FF] tw-px-4 tw-py-3.5 lg:tw-items-center">
      <div className="tw-absolute -tw-bottom-1.5 tw-left-3">
        <div className="tw-absolute -tw-top-1.5 !tw-z-10 tw-h-2 tw-w-2 tw-bg-[#F1F4FF]"></div>
        <div className="tw-h-2 tw-w-2 tw-rotate-45 tw-bg-primary"></div>
      </div>
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-5 lg:tw-flex-row lg:tw-items-center">
        <div className="tw-flex tw-items-center tw-gap-5">
          <p className="tw-space-x-2 tw-text-sm">
            <span className="tw-inline-block">{t('common.selected')}</span>
            <span className="tw-font-medium">{selectedGroup.ids.length}</span> /
            {totalCount ?? 0}
          </p>
          {onEdit && (
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-3 tw-py-1.5 hover:tw-opacity-90"
              onClick={onEdit}
            >
              <p className="tw-text-sm tw-font-medium tw-text-white">
                {t('common.edit')}
              </p>
            </div>
          )}
          {onDuplicate && (
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-3 tw-py-1.5 hover:tw-opacity-90"
              onClick={onDuplicate}
            >
              <p className="tw-text-sm tw-font-medium tw-text-white">
                {t('addCompany.createDbDuplicate')}
              </p>
            </div>
          )}
          <div className="tw-relative tw-z-[100]">
            {isSelectedActive ? (
              <Tooltip
                text={t('addCompany.deactivate')}
                onClick={() => handleActivate(false)}
              >
                <FaIcon icon={faCircleXRegular} className='tw-text-primary tw-w-4 tw-h-4'/>

              </Tooltip>
            ) : (
              <Tooltip
                text={t('addCompany.activate')}
                onClick={() => handleActivate(true)}
              >
                <FaIcon icon={faCircleDashedRegular} className='tw-text-primary tw-w-4 tw-h-4'/>
              </Tooltip>
            )}
          </div>
          {canRemoveCompany && (
            <div className="tw-relative tw-z-[100]">
              <Tooltip text={t('common.delete')} onClick={handleDelete}>
                <FaIcon
                  icon={faTrash}
                  className="tw-h-4 tw-w-4 tw-cursor-pointer tw-text-primary"
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <div className="tw-relative tw-cursor-pointer" onClick={() => resetAll()}>
        <div className="tw-absolute -tw-top-1.5 tw-left-1/2 tw-z-0 tw-h-7 tw-w-7 tw--translate-x-1/2 tw-rounded-full tw-bg-white tw-p-2 tw-shadow lg:tw-top-1/2 lg:tw--translate-y-1/2"></div>
        <img className="tw-z-1 tw-relative" src={CloseGray} alt="Close Icon" />
      </div>
    </div>
  )
}

export default CompanySelectedActionBar
